import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState({ app_env: "preview", is_production: false, phone_otp_enabled: true, demo_mode: true });

  useEffect(() => {
    api.get("/config").then(({ data }) => setConfig(data)).catch(() => {});
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    const token = localStorage.getItem("ll_token");
    if (!token) {
      setLoading(false);
      return;
    }
    refreshUser().finally(() => setLoading(false));
  }, [refreshUser]);

  const persist = (data) => {
    localStorage.setItem("ll_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const login = async (email, password) => persist((await api.post("/auth/login", { email, password })).data);
  const register = async (payload) => persist((await api.post("/auth/register", payload)).data);
  const googleLogin = async (session_id) => persist((await api.post("/auth/google/session", { session_id })).data);
  const requestOtp = async (phone) => (await api.post("/auth/otp/request", { phone })).data;
  const verifyOtp = async (phone, code, full_name) => {
    const { data } = await api.post("/auth/otp/verify", { phone, code, full_name });
    persist(data);
    return data;
  };

  const adminLogin = async (email, password) => (await api.post("/auth/admin/login", { email, password })).data;
  const adminVerify2fa = async (challenge_id, code) =>
    persist((await api.post("/auth/admin/verify-2fa", { challenge_id, code })).data);
  const resendVerification = async () => (await api.post("/auth/resend-verification")).data;

  const logout = async () => {
    localStorage.removeItem("ll_token");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, config, login, register, googleLogin, requestOtp, verifyOtp, adminLogin, adminVerify2fa, resendVerification, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
